import { useState } from 'react';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import {
  Ingredient,
  IngredientVariant,
  PointsPlacementVariant,
  type IngredientViewModel,
} from '../../../../Ingredient/Ingredient';
import { getSwappableIngredients } from '../../../swap/getSwappableIngredients';
import { IngredientContainer } from '../../IngredientContainer/IngredientContainer';
import styles from '../../IngredientEditor.module.css';

interface Props {
  testid?: string;
  ingredient: IngredientViewModel;
  pairingIngredientIds: number[];
  swappablePairings: Pairing[];
  onBackButtonClick: () => void;
  onIngredientSelected: (params: {
    sourceIngredientId: number;
    targetIngredientId: number;
    targetPairing: Pairing;
  }) => void;
  isModalEditor?: boolean;
  isCustomModalEditor?: boolean;
  pairingPoints: number;
  updateButton?: (
    onSelect: () => void,
    points: string,
    disabled?: boolean
  ) => JSX.Element;
}

export function IngredientSwap(props: Props) {
  const {
    onIngredientSelected,
    testid,
    ingredient,
    pairingIngredientIds,
    swappablePairings,
    isModalEditor = false,
    pairingPoints,
    updateButton,
    isCustomModalEditor,
  } = props;
  const { id: ingredientId } = ingredient;
  const [currentIngredient, setCurrentIngredient] = useState(ingredient);
  const { ingredients: swappableIngredients, pairings: swapPairingsMap } =
    getSwappableIngredients({
      ingredientId,
      pairingIngredientIds,
      swappablePairings,
    });

  const updatedPoints = swapPairingsMap[currentIngredient.id]
    ? swapPairingsMap[currentIngredient.id].points
    : pairingPoints;

  return (
    <IngredientContainer testid={testid}>
      <Ingredient
        variant={IngredientVariant.Swap}
        ingredient={ingredient}
        selected={currentIngredient.id === ingredientId}
        onSelect={() => setCurrentIngredient(ingredient)}
        pointsPlacementVariant={PointsPlacementVariant.Swap}
      />
      {swappableIngredients.map((ingredient, i) => {
        const { id: swapIngredientId } = ingredient;
        const isLastIngredient = i === swappableIngredients.length - 1;
        return (
          <Ingredient
            testid={`${testid}-ingredient-swap-${swapIngredientId}`}
            onSelect={() => {
              isModalEditor && !isCustomModalEditor
                ? setCurrentIngredient(ingredient)
                : onIngredientSelected({
                    sourceIngredientId: ingredientId,
                    targetIngredientId: swapIngredientId,
                    targetPairing: swapPairingsMap[swapIngredientId],
                  });
            }}
            key={swapIngredientId}
            variant={IngredientVariant.Swap}
            ingredient={ingredient}
            selected={currentIngredient.id === swapIngredientId}
            pointsPlacementVariant={PointsPlacementVariant.Swap}
            className={isLastIngredient ? styles['last-ingredient'] : ''}
          />
        );
      })}
      {isModalEditor &&
        (updateButton
          ? updateButton(
              () =>
                onIngredientSelected({
                  sourceIngredientId: ingredientId,
                  targetIngredientId: currentIngredient.id,
                  targetPairing: swapPairingsMap[currentIngredient.id],
                }),
              updatedPoints.toString(),
              currentIngredient.id === ingredientId
            )
          : null)}
    </IngredientContainer>
  );
}
